/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { ILocalDataAccessLayer, IMapConfig } from "./local-data-access-layer.interface";

/**
 * Class for data access
 */
export abstract class AbstractLocalDataAccessLayer<T, MapKeys extends string = string> implements ILocalDataAccessLayer<
  T,
  MapKeys
> {
  /**
   * @inheritdoc
   */
  private _entities: T[] = [];

  /**
   * Cache for mapped entities. Avoids calling mapEntity() (which typically
   * instantiates a new class, e.g. `new Report(entity)`) on every access.
   * Keyed by entity id for O(1) lookup.
   */
  private _mappedEntityCache = new Map<string, T>();

  /**
   * Monotonically increasing counter that changes on every data mutation
   * (set, delete, clear). Used by external caches (e.g. Fuse) to detect staleness.
   */
  private _generation = 0;

  /**
   * Returns the current generation counter. Incremented on every data mutation.
   */
  get generation(): number {
    return this._generation;
  }

  /**
   * A local map of the position of entities.
   */
  private _map = new Map<string, number>();

  /**
   * @inheritdoc
   */
  readonly mapsConfig = {} as Record<MapKeys, IMapConfig>;

  /**
   * List of maps to access entities by different keys configured in mapsConfig
   */
  protected _maps = {} as Record<MapKeys, Map<string, number[]>>;

  /**
   * Cached maps accessor object. Invalidated when entities change.
   */
  private _mapsAccessor: Record<MapKeys | "id", { get: (key: string) => T[] }> | null = null;

  /**
   * Object containing maps to access entities by different keys configured in mapsConfig
   */
  get maps(): Record<MapKeys | "id", { get: (key: string) => T[] }> {
    if (this._mapsAccessor) {
      return this._mapsAccessor;
    }

    const map = {
      id: {
        get: (id: string) => {
          const found = this.get(id);

          if (found !== undefined) {
            return [found];
          } else {
            return [];
          }
        }
      }
    } as Record<MapKeys | "id", { get: (key: string) => T[] }>;

    // from the configuration, not from `_maps`: those are only filled once entities are set, and a
    // map that is read before that must return nothing instead of being missing from the accessor
    (Object.keys(this.mapsConfig) as MapKeys[]).forEach((key) => {
      map[key] = {
        get: (id: string) => this.getFromMap(id, key)
      };
    });

    this._mapsAccessor = map;

    return map;
  }

  /**
   * @inheritdoc
   */
  abstract getIdentifier(entity: T): string;

  /**
   * Maps an entity to a new entity.
   * Can be used to extend the entity with additional properties, e.g. "billing.entity" adds additional getters and fetch methods
   * It can be overwritten by the concrete data access layer, default behavior does not change the entity.
   *
   * @param entity
   */
  protected mapEntity(entity: T): T {
    return entity;
  }

  /**
   * Returns the mapped entity for the given raw entity, using cache when available.
   * Only calls mapEntity() when the entity is not yet cached.
   */
  private getMappedEntity(entity: T): T {
    const id = this.getIdentifier(entity);
    const cached = this._mappedEntityCache.get(id);
    if (cached !== undefined) {
      return cached;
    }

    const mapped = this.mapEntity(entity);
    this._mappedEntityCache.set(id, mapped);
    return mapped;
  }

  /**
   * @inheritdoc
   */
  get(id: string): T | undefined {
    const pos = this._map.get(id);
    if (pos === undefined) {
      return undefined;
    }

    const mappedEntity = this.getMappedEntity(this._entities[pos]);

    return mappedEntity || undefined;
  }

  /**
   * Returns all entities that have the given id in one of the maps.
   *
   * @param id
   * @param mapKey
   * @returns
   */
  private getFromMap(id: string, mapKey: MapKeys): T[] {
    const getIdentifier = this.mapsConfig[mapKey].getIdentifier;

    if (!this._maps[mapKey]) {
      this._maps[mapKey] = new Map();
    }

    // get all indexes of entities referencing the given id
    const foundIndexes: number[] = this._maps[mapKey].get(id) ?? [];

    // get entities by index and map it to the frontend class
    const foundEntities = foundIndexes.map((index: number) => {
      const entity = this.getMappedEntity(this._entities[index]);
      return { entity, index };
    });

    // make sure that the identifier of the entity includes the given id, in case the entity was updated in the meantime
    const matchingEntities: T[] = [];
    const matchingIndexes: number[] = [];
    foundEntities.forEach((found) => {
      const { entity, index } = found;
      const isMatch = getIdentifier(entity).includes(id);
      if (isMatch) {
        matchingEntities.push(entity);
        matchingIndexes.push(index);
      }
    });

    // update the map with the filtered indexes
    this._maps[mapKey].set(id, matchingIndexes);

    return matchingEntities;
  }

  /**
   * Updates the secondary maps (mapsConfig) for a single entity at a given position.
   */
  private updateMapsForEntity(entity: T, pos: number): void {
    for (const entry of Object.entries(this.mapsConfig)) {
      const name = entry[0] as MapKeys;
      const { getIdentifier } = entry[1] as IMapConfig;
      if (!this._maps[name]) {
        this._maps[name] = new Map();
      }

      for (const identifier of getIdentifier(entity) ?? []) {
        const found = this._maps[name].get(identifier) ?? [];
        if (!found.includes(pos)) {
          found.push(pos);
        }
        this._maps[name].set(identifier, found);
      }
    }
  }

  /**
   * @inheritdoc
   */
  set(entity: T): T {
    const id = this.getIdentifier(entity);
    let pos = this._map.get(id);

    // Invalidate cached mapped entity and maps accessor on mutation
    this._mappedEntityCache.delete(id);
    this._mapsAccessor = null;
    this._generation++;

    if (pos === undefined) {
      this._entities.push(entity);
      this._map.set(id, this._entities.length - 1);
      pos = this._entities.length - 1;
    } else {
      this._entities.splice(pos, 1, entity);
    }

    this.updateMapsForEntity(entity, pos);

    return this._entities[pos] as T;
  }

  /**
   * @inheritdoc
   */
  delete(entity: T): T {
    const id = this.getIdentifier(entity);

    const indexOfDeleted = this._map.get(id);
    if (indexOfDeleted !== undefined) {
      this._entities.splice(indexOfDeleted, 1);

      // Rebuild the id map for all entities after the deleted index
      this._map.delete(id);
      for (let i = indexOfDeleted; i < this._entities.length; i++) {
        this._map.set(this.getIdentifier(this._entities[i]), i);
      }

      // Rebuild secondary maps from scratch (necessary because positions shifted)
      Object.keys(this.mapsConfig).forEach((mapName) => this._maps[mapName].clear());
      for (let i = 0; i < this._entities.length; i++) {
        this.updateMapsForEntity(this._entities[i], i);
      }

      // Invalidate caches
      this._mappedEntityCache.delete(id);
      this._mapsAccessor = null;
      this._generation++;
    }

    return this.mapEntity(entity);
  }

  /**
   * @inheritdoc
   */
  clear() {
    this._map.clear();
    for (const entry of Object.entries(this.mapsConfig)) {
      const name = entry[0] as MapKeys;
      this._maps[name]?.clear();
    }
    this._entities.splice(0);
    this._mappedEntityCache.clear();
    this._mapsAccessor = null;
    this._generation++;
  }

  /**
   * @inheritdoc
   */
  get entities() {
    return this._entities.map((e) => this.getMappedEntity(e));
  }

  /**
   * @inheritdoc
   */
  set entities(entities: T[]) {
    this.clear();
    this._entities.push(...entities);
    this._entities.forEach((entity, index) => {
      this._map.set(this.getIdentifier(entity), index);
      this.updateMapsForEntity(entity, index);
    });
  }
}
